import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Globe, Search, MapPin, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OrganizersListPage() {
  const [organizers, setOrganizers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    loadOrganizers();
  }, []);

  const loadOrganizers = async (q = "") => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("search", q);
      const res = await axios.get(`${API}/organizers?${params}`);
      setOrganizers(res.data);
    } catch {
      setOrganizers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadOrganizers(search);
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Hero */}
      <div className="bg-[#1B4F72] text-white py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h1 className="text-3xl md:text-4xl font-bold font-serif mb-3">Fuar Organizatörleri</h1>
          <p className="text-white/80 max-w-xl mx-auto">
            Türkiye ve dünyadan fuar organizatörleri. Etkinlikler, fuar takvimleri ve daha fazlası.
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Search */}
        <form onSubmit={handleSearch} className="flex gap-2 mb-8 max-w-md mx-auto">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Organizatör adı veya şehir ara..."
            className="flex-1"
          />
          <Button type="submit" className="bg-[#1B4F72] hover:bg-[#154360] gap-1.5">
            <Search className="w-4 h-4" /> Ara
          </Button>
        </form>

        {/* Results */}
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-stone-300" />
          </div>
        ) : organizers.length === 0 ? (
          <div className="text-center py-16">
            <Globe className="w-16 h-16 text-stone-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-stone-700 mb-2">Organizatör Bulunamadı</h2>
            <p className="text-stone-500 mb-6">Bu kriterlere uygun organizatör henüz kayıt olmamış.</p>
            <Link to="/organizator-giris">
              <Button className="bg-[#1B4F72] hover:bg-[#154360]">Organizatör Olarak Kayıt Ol</Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {organizers.map((org) => (
              <Link
                key={org.slug}
                to={`/organizator/${org.slug}`}
                className="bg-white rounded-xl border border-stone-200 p-5 hover:shadow-md hover:border-[#1B4F72]/30 transition-all group"
              >
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-lg bg-stone-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {org.logo_url ? (
                      <img src={org.logo_url} alt="" className="w-full h-full object-contain p-1" />
                    ) : (
                      <Globe className="w-6 h-6 text-stone-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-stone-900 group-hover:text-[#1B4F72] transition-colors truncate">
                      {org.org_name}
                    </h3>
                    {org.city && (
                      <p className="text-xs text-stone-500 flex items-center gap-1 mt-1">
                        <MapPin className="w-3 h-3" /> {org.city}
                      </p>
                    )}
                    {org.about && (
                      <p className="text-xs text-stone-500 mt-2 line-clamp-2">{org.about}</p>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="mt-12 bg-[#1B4F72] text-white rounded-xl p-8 text-center">
          <h2 className="text-xl font-bold font-serif mb-2">Organizatör Olun</h2>
          <p className="text-white/80 mb-4">Fuarlarınızı tanıtın, etkinlik takvimi paylaşın, sektöre ulaşın.</p>
          <Link to="/organizator-giris">
            <Button variant="secondary" className="bg-white text-[#1B4F72] hover:bg-stone-100">
              KAYIT OL
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
